/**
 * Main Header component
 * 
 * See https://w3c.github.io/uievents/#dom-uievent-which for key codes
 * 
 * Keyboard Navigation:
 *   - ENTER key (13)
 *     - on Nav Item Link: not handled, leave to browser default.
 *     - on Parent Nav Item:
 *       - on Mobile: expand/collapse the Sub Menu.
 *       - on Desktop:
 *         - if Sub Menu showing: leave to browser default (follow the link).
 *         - if Sub Menu not showing: show Sub Menu.
 *   - SPACE key (32)
 *     - on Mobile: expand/collapse the Sub Menu.
 *     - on Desktop: 
 *       - on Parent Nav Item: show/hide the Sub Menu.
 *       - on Nav Item Link: not handled, leave to browser default (scroll page)
 *   - ARROW DOWN (40)
 *     - on Mobile: not handled, leave to browser default.
 *     - on Desktop: show the Sub Menu if it's not already showing, otherwise leave to browser default.
 *   - ARROW UP (38)
 *     - on Mobile: not handled, leave to browser default.
 *     - on Desktop: hide the Sub Menu if it's currently showing, otherwise leave to browser default.
 * 
 * Browsers default:
 *   - SPACE: scrolls down one page 
 *   - ARROW UP: scrolls up the page a bit 
 *   - ARROW DOWN: scrolls down the page a bit
 * 
 * ARROW keys override:
 *   - ARROW DOWN and ARROW UP are used only open/close Sub Menu on Desktop not to navigate around the Nav Items.
 *     If we handle ARROW keys (up/down/left/right) to move around the Nav Items, things will
 *       become far more complicated because we then also have to deal with tabIndex, i.e.:
 *       We have to determine what's which element to focus if user press ARROW RIGHT key on last Menu Item.
 * 
 * @module components/header-main
 */

import Component from './component';
import utils from '../utils';
import transitionEnd from '../utils/transition-end';
import pointInPolygon from 'point-in-polygon';

// classes
const CSS_HOVER = '-hover';
const CSS_HOVERING = '-hovering';
const CSS_HOVERING_OFF = '-unhovering';
const CSS_EXPAND = '-expand';
const CSS_EXPANDING = '-expanding';
const CSS_EXPANDING_OFF = '-unexpanding';
const CSS_PARENT = '-parent';
const CSS_MOBILE_NAV_ACTIVE = '-mobile-nav-active';
const CSS_ACTIVE = '-active';
const CSS_SEARCH_ACTIVE = '-search-active';

// aria
const ARIA_HIDDEN = 'aria-hidden';

// data key
const DATA_ELEMENT_TRANSITION = 'header-main-element-transition';

// whether or not to open menu when it's parent got focus
// DEV NOTE: DO NOT ENABLE THIS WITHOUT PROPER TESTING
const EXPAND_ON_FOCUS = false;

// NOTE: SEARCH_MOVEMENT_CONE (SMC) also works on menu items
// workaround to prevent closing the search form the mouse pointer moving from the Search icon click point to search form.
// enable/disable this feature
const SEARCH_MOVEMENT_CONE = true; 
// extra Y offset to clicked point to make the cone a bit bigger
const SEARCH_MOVEMENT_THRESHOLD = 10;
// after mouse started moving, cancel the cone when the mouse stopped moving for specific duration (ms)
const SEARCH_MOVEMENT_TIMEOUT = 200;
// enable/disable debug. 
//  - run `___smcDebug=true` on console to show the cone
//  - run `___smcDebug='disable'` on disable this feature
const SEARCH_MOVEMENT_DEBUG = true;

export default (($, /* eslint-disable */ window, undefined /* eslint-enable */) => {
  'use strict';

  const document = window.document;

  const reflowElement = element => {
    // trigger getComputedStyles
    $(element).css('opacity');

    // return $(element)[0].offsetHeight;
  };

  /**
   * HeaderMain component class.
   */
  class HeaderMain extends Component {
    /**
     * Constructor
     */
    constructor(element, options = {}) {
      super(element, options);
      this.name = 'header-main';
    }

    /**
     * Main initializer.
     * 
     * @returns void
     */
    init() {
      const el = this.element;

      if (el.hasClass(this.initializedClass)) {
        // only need to initialize this component once
        return;
      }

      // mark as initialized
      el.addClass(this.initializedClass);

      // states
      this.wasMobile = undefined;
      this.wasClickMode = undefined;
      this.noHover = utils.feature.noHover;

      // cache some elements
      this.nav = $(HeaderMain.selectors.nav, el);
      this.mobileNavToggleButton = $(HeaderMain.selectors.mobileNavToggleButton, el);

      this.flyout = $(HeaderMain.selectors.flyout, el);
      this.flyoutNav = $(HeaderMain.selectors.flyoutNav, this.flyout);
      this.flyoutSearch = $(HeaderMain.selectors.flyoutSearch, this.flyout);
      this.flyoutSearchToggleButton = $(HeaderMain.selectors.flyoutSearchToggleButton, this.flyoutSearch);
      this.flyoutSocial = $(HeaderMain.selectors.flyoutSocial, this.flyout);
      
      this.firstlevelMenu = $('> ul', this.nav);
      this.megaMenuItems = $(`${HeaderMain.selectors.megaMenuItems} > li`, el);

      this.searchFormContainer = $(HeaderMain.selectors.searchFormContainer, el);
      this.searchForm = $(HeaderMain.selectors.searchForm, el);
      this.searchFormInput = $(HeaderMain.selectors.searchFormInput, el);
      this.mobileSearchCloseButton = $(HeaderMain.selectors.mobileSearchCloseButton, this.searchForm);

      // init UX
      Promise.all([
        this.initNav(),
        this.initSearch(),
        this.initFocus(),
        this.initKeyboard()
      ]).then(() => {
        // initial Desktop/Mobile mode setup
        this._onResize(null, true);
      });
    }

    /**
     * Initialize navigation UX.
     */
    async initNav() {
      const el = this.element;

      // keep list of nav item currently in hover state
      this.navHoverList = [];

      // whether or not we are entraping focus inside the component (Mobile only)
      this._entrapingFocus = false;

      // add CSS_PARENT class to all menu items with sub items
      $('li > ul, li > nav', this.nav).parent().addClass(CSS_PARENT);

      // mobile nav toggle click
      el.on('click', HeaderMain.selectors.mobileNavToggleButton, (ev) => {
        this._toggleMobileNav();
      });

      // handle: Desktop/Mobile mode changing
      window.addEventListener('resize', utils.debounce(this._onResize.bind(this), 200));

      this.initNavAutoClose();
    }

    /**
     * Nav and search form auto close.
     */
    async initNavAutoClose() {
      if (!this.__onDocumentPointerDown) {
        this.__onDocumentPointerDown = utils.debounce(this._onDocumentPointerDown.bind(this), 13);
      }

      // 'pointerdown' does not work
      window.addEventListener('mousedown', this.__onDocumentPointerDown, false);
      window.addEventListener('touchstart', this.__onDocumentPointerDown, false);
    }

    /**
     * Initialize search UX.
     */
    async initSearch() {
      const el = this.element;

      // update form submit button state
      const updateFormState = (enable) => {
        if (enable === undefined) {
          const t = this.searchFormInput;
          enable = (t.val() !== '');
        }

        this.searchForm.find('input[type="submit"], button[type="submit"]').prop('disabled', !(enable));
      };

      // watch search toggle click
      el.on('click', HeaderMain.selectors.flyoutSearchToggleButton, (ev) => {
        if (!this.element.hasClass(CSS_SEARCH_ACTIVE)) {
          this.searchFormContainer.addClass(CSS_EXPANDING);
          reflowElement(this.searchFormContainer);
          this.searchFormInput.get(0).focus();
          this._toggleSearch(true, null, ev);

          // fix scroll caused by focus when the search form is off screen
          $('#page').scrollLeft(0);
        }
        else {
          this._toggleSearch(false);
        }
      });

      // watch search form close click
      el.on('click', HeaderMain.selectors.mobileSearchCloseButton, (ev) => {
        this._toggleSearch(false);
      });

      // watch search form back button click
      el.on('click', HeaderMain.selectors.searchFormBackButton, (ev) => {
        this._toggleSearch(false);
      });

      // watch search form submit
      el.on('submit', HeaderMain.selectors.searchForm, (ev) => {
        const inp = this.searchFormInput.first();
        if (inp.length && inp.val() === '') {
          ev.preventDefault();
        }
      });

      // watch search input changes to update submit button state
      el.on('input', HeaderMain.selectors.searchFormInput, (ev) => {
        const t = $(ev.currentTarget);

        if (t.is(HeaderMain.selectors.searchFormInput)) {
          updateFormState(t.val() !== '');
        }
      });

      // initial form state
      updateFormState();
    }

    /**
     * Initialize keyboard focus UX.
     */
    async initFocus() {
      if (!this.__onNavLinkFocusIn) {
        this.__onNavLinkFocusIn = this._onNavLinkFocusIn.bind(this);
        this.__onNavLinkFocusOut = this._onNavLinkFocusOut.bind(this);
        this.__onSearchFormFocusOut = this._onSearchFormFocusOut.bind(this);
      }

      this.nav.off('focusin', 'a', this.__onNavLinkFocusIn);
      this.nav.off('focusout', 'a', this.__onNavLinkFocusOut);
      this.searchForm.off('focusout', '*', this.__onSearchFormFocusOut);

      if (!this.isMobile()) {
        this.nav.on('focusin', 'a', this.__onNavLinkFocusIn);
        this.nav.on('focusout', 'a', this.__onNavLinkFocusOut);
        this.searchForm.on('focusout', '*', this.__onSearchFormFocusOut);
      }
    }

    /**
     * Initialize keyboard stroke UX
     */
    async initKeyboard() {
      if (!this.__handleKeyCodes) {
        // Supported keys:
        // ENTER, SPACE, ARROW UP, ARROW DOWN
        this.__handleKeyCodes = [13, 32, 38, 40];
      }

      this.element.on('keydown', (ev) => {
        // handle: <A> element inside main nav
        if (ev.target.nodeType === 1 && ev.target.nodeName.toLowerCase() === 'a' && $.contains(this.nav[0], ev.target)) {
          const keyCode = ev.which;

          // if not a supported keyCode, ignore and leave to browser default
          if (!this.__handleKeyCodes.includes(keyCode)) {
            return;
          }

          const t = $(ev.target);
          const item = t.closest('li');
          const isParent = item.hasClass(CSS_PARENT);

          // if not a parent item, leave to browser default
          if (!isParent) {
            return;
          }

          const isMobile = this.isMobile();
          const isHover = item.hasClass(CSS_HOVER) && !isMobile;

          // State: Mobile
          // UX: SPACE or ENTER on Parent
          // Action: 
          //   - Prevent browser default
          //   - Toggle expand/collapse
          if (isMobile && (keyCode === 13 || keyCode === 32)) {
            ev.preventDefault();
            this._toggleItemExpand(item);
            return;
          }

          // State: Mobile
          // UX: ARROW key
          // Action: Ignore, leave to browser default
          if (isMobile) {
            return;
          }

          // State: Desktop, Sub Menu OFF
          // UX: Any supported keys
          // Action: 
          //   - Prevent browser default. 
          //   - If not ARROW UP, show Sub Menu.
          if (!isHover) {
            ev.preventDefault();

            if (keyCode !== 38) {
              this._addNavHoverList(item);
            }
          }
          // State: Desktop, Sub Menu ON
          // UX: Any supported keys except ENTER
          // Action: 
          //   - Prevent browser default. 
          //   - If not ARROW DOWN, hide Sub Menu.
          else if (isHover && keyCode !== 13) {
            ev.preventDefault();

            if (keyCode !== 40) {
              this._removeNavHoverList(item);
            }
          }
        }
      });
    }

    // ------------------------------ //
    // ---- Search Movement Cone ---- //
    /**
     * Search Movement Cone Start
     * 
     * @param {Event} ev
     * @param {jQuery} item jQuery Element
     * @param {jQuery} container jQuery Element
     */
    smcStart(ev, item, container) {
      if (!SEARCH_MOVEMENT_CONE || !ev || !container) {
        return;
      }

      if (window.___smcDebug === 'disable') {
        return;
      }

      //const level = this._getMenuLevel(container);
      //const o = container.offset();
      //const x2 = Math.max(o.left + container.outerWidth(), ev.pageX);
      
      this._smcData = {
        pt: {
          x: ev.pageX,
          y: ev.pageY
        },
        polygon: this.smcPolygon(ev, item, container)
      };

      this._smcTarget = item;
      this._smcSuppressedMenuHover = ev;

      this._smcMove = this._smcMove || this.smcMove.bind(this);
      this._smcSuppressMenuHover = true;
      this.smcDebug();

      document.addEventListener('mousemove', this._smcMove, true);
    }

    /**
     * Search Movement Cone Stop
     * 
     * @param {jQuery} item jQuery Element
     * @param {jQuery} container jQuery Element
     */
    smcStop(item, container) {
      if (item !== undefined && this._smcTarget) {
        if (item[0] !== this._smcTarget[0]) {
          return;
        }
      }

      this._smcTarget = null;
      this.smcDebug(false);
      
      this._smcSuppressMenuHover = false;
      this._smcSuppressedMenuHover = null;

      if (this._smcMoveTimeout) {
        window.clearTimeout(this._smcMoveTimeout);
      }
      document.removeEventListener('mousemove', this._smcMove, true);
    }

    /**
     * Track mouse movement when SMC is active.
     * 
     * @param {Event} ev 
     * @returns 
     */
    smcMove(ev) {
      const pt = [ev.pageX, ev.pageY];

      // if moved outside of the threshold polygon
      if (!pointInPolygon(pt, this._smcData.polygon)) {
        // stop tracking movement
        this.smcStop();
        
        // if the mouse pointer is still inside the NAV
        if (ev.target === this.flyoutNav[0] || $.contains(this.flyoutNav[0], ev.target, ev.currentTarget)) {
          let t = $(ev.target);
          if (!t.is('li')) {
            t = t.closest('li');
          }

          // if mouse pointer is on top of menu item, trigger hover on it
          if (t.length) {
            this.smcClearTimer();
            this._onNavItemMouseEnter(new $.Event(ev, {
              currentTarget: t[0]
            }));
          }
          
        }
        // if the mouse pointer is outside the NAV
        else {
          this._clearNavHoverList();
        }
        
        return;
      }
      // if mouse pointer is inside the threshold polygon
      else {
        // save the menu item that being suppressed
        let t = $(ev.target);
        if (!t.is('li')) {
          t = t.closest('li');
        }
        this._smcSuppressedMenuHover = $.Event(ev, {
          currentTarget: t[0]
        });
      }

      // set threashold timeout, when timeout, the menu item thas 
      //   was suppressed will get the hover
      this.smcSetTimer(this._smcSuppressedMenuHover);
    }

    /**
     * Setup SMC timer.
     * 
     * @param {Event} ev 
     */
    smcSetTimer(ev) {
      this.smcClearTimer();
      this._smcMoveTimeout = window.setTimeout(() => {
        this.smcStop();
        if (ev) {
          if ($(ev.currentTarget).is(HeaderMain.selectors.flyoutSearchToggleButton)) {
            this.smcStart(ev, this.flyoutSearchToggleButton, this.searchFormContainer);
          }
          else {
            this._onNavItemMouseEnter(ev);
          }
        }
        else {
          this._clearNavHoverList();
        }
      }, SEARCH_MOVEMENT_TIMEOUT);
    }

    /**
     * Clear SMC timer.
     */
    smcClearTimer() {
      if (this._smcMoveTimeout) {
        window.clearTimeout(this._smcMoveTimeout);
      }
    }

    /**
     * Create SMC threshold polygon.
     * 
     * @param {Event} ev 
     * @param {jQuery} item
     * @param {jQuery} container 
     * @returns 
     */
    smcPolygon(ev, item, container) {
      const level = this._getMenuLevel(container);
      const itemOffset = item.offset();
      const itemW = item.outerWidth();
      const itemH = item.outerHeight();
      const span = $('> a > span, > span', item);
      const spanOffset = span.offset();
      const o = container.offset();
      const w = container.outerWidth();
      const h = container.outerHeight();
      
      let ret, x1, y1;

      if (level <= 2) {
        x1 = ev.pageX;
        x1 = Math.min(Math.max(ev.pageX, itemOffset.left), itemOffset.left + itemW);

        y1 = Math.min(ev.pageY, itemOffset.top + itemH);
        y1 = Math.max(y1 - SEARCH_MOVEMENT_THRESHOLD, itemOffset.top);

        if (spanOffset.top > y1) {
          ret = [
            [
              Math.max(itemOffset.left, x1 - (SEARCH_MOVEMENT_THRESHOLD/2)),
              y1
            ],
            [
              Math.min(itemOffset.left + itemW, x1 + (SEARCH_MOVEMENT_THRESHOLD/2)),
              y1
            ]
          ];
        }
        else {
          ret = [
            [
              x1,
              y1
            ]
          ];
        }
        

        if (spanOffset.top <= y1) {
          ret.push(
            [
              itemOffset.left + itemW,
              Math.max(spanOffset.top, y1)
            ]
          );
        }

        ret.push(
          [
            o.left + w,
            o.top
          ]
        );

        ret.push(
          [
            o.left,
            o.top
          ]
        );

        if (spanOffset.top <= y1) {
          ret.push(
            [
              itemOffset.left,
              Math.max(spanOffset.top, y1)
            ],
          );
        } 
      }
      else {
        // right aligned (submenu on the left)
        if (o.left < itemOffset.left) {
          x1 = Math.max(o.left + w - SEARCH_MOVEMENT_THRESHOLD, ev.pageX);
        }
        // left aligned (submenu on the right)
        else {
          x1 = Math.min(o.left, ev.pageX);
        }

        y1 = Math.min(ev.pageY, itemOffset.top + itemH);
        
        ret = [
          [
            x1,
            itemOffset.top
          ],
          [
            x1,
            itemOffset.top + itemH
          ]
        ];

        // right aligned (submenu on the left)
        if (o.left < itemOffset.left) {
          ret.push(
            [
              o.left + w - SEARCH_MOVEMENT_THRESHOLD,
              o.top + h
            ],
            [
              o.left + w - SEARCH_MOVEMENT_THRESHOLD,
              o.top
            ]
          );
        }
        else if (o.left > itemOffset.left) {
          ret.push(
            [
              o.left + SEARCH_MOVEMENT_THRESHOLD,
              o.top + h
            ],
            [
              o.left + SEARCH_MOVEMENT_THRESHOLD,
              o.top
            ]
          );
        }
      }

      return ret;
    }

    /**
     * SMC debug polygon.
     * 
     * @param {Boolean} show 
     * @returns 
     */
    smcDebug(show) {
      if (SEARCH_MOVEMENT_DEBUG && window.___smcDebug && window.___smcDebug !== 'disable') {
        if (this._smcDebugCanvas) {
          this._smcDebugCanvas.remove();
        }
        
        if (show === false || !this._smcData) {
          return;
        }

        this._smcDebugCanvas = $('<canvas>').attr({
          width: $(window).width(),
          height: $(window).height()
        }).appendTo('body');

        this._smcDebugCanvas.css({
          'pointer-events': 'none',
          'position': 'absolute',
          'top': 0,
          'left': 0,
          'z-index': 999999
        }).show();

        if (!this._smcDebugCanvas[0].getContext) {
          return;
        }

        const ctx = this._smcDebugCanvas[0].getContext('2d');
        ctx.strokeStyle = 'red';
        ctx.lineWidth = 2;
        ctx.beginPath();
        
        ctx.moveTo(this._smcData.polygon[0][0], this._smcData.polygon[0][1]);
        for (var i = 1; i < this._smcData.polygon.length; i++) {
          ctx.lineTo(this._smcData.polygon[i][0], this._smcData.polygon[i][1]);
        }

        ctx.lineTo(this._smcData.polygon[0][0], this._smcData.polygon[0][1]);

        ctx.stroke();
      }
    }

    // ---- //Search Movement Cone ---- //
    // -------------------------------- //

    /**
     * Get menu level of the specified element.
     * 
     * @param {HTMLElement} element 
     */
    _getMenuLevel(element) {
      let el = $(element);
      let isMegaMenuItem = false;
      let p;
      let l = 0;

      if (el[0] === this.nav[0] || el[0] === this.firstlevelMenu[0]) {
        return 1;
      }

      if (!$.contains(this.nav[0], el[0])) {
        return 0;
      }

      $(HeaderMain.selectors.megaMenuItems, this.element).each((idx, elem) => {
        if (elem === el[0] || $.contains(elem, el[0])) {
          isMegaMenuItem = true;
          el = $(elem);
          return false;
        }
      });

      if (isMegaMenuItem) {
        return 2;
      }

      p = el.closest('ul');
      l = 1;

      while (p && p.length && p[0] !== this.firstlevelMenu[0] && p[0] !== this.nav[0]) {
        l++;
        p = p.parent().closest('ul');
      }

      return l;
    }


    /**
     * Nav Item link focus in.
     * 
     * @param {Event} ev 
     */
    _onNavLinkFocusIn(ev) {
      if (this.isMobile()) {
        return;
      }

      if (!EXPAND_ON_FOCUS) {
        const t = $(ev.currentTarget);
        t.closest('li').siblings().filter((idx, elem) => $(elem).hasClass(CSS_PARENT)).each((idx, elem) => {
          this._removeNavHoverList($(elem));
        });
      }

      if (EXPAND_ON_FOCUS) {
        // same logic with pointer up
        this._onNavItemLinkPointerUp(ev);

        // handle: reverse TAB
        // traverse up the link that get focus to top of the tree
        const t = $(ev.currentTarget);
        let stack = [];
        let li = t.closest('li');
        let ul = li.parent();

        stack.push(li);

        while (ul.length && ul[0] !== this.firstlevelMenu[0]) {
          li = ul.closest('li');
          ul = li.parent();

          stack.push(li);
        }

        // add to hover list from parent
        stack.reverse();
        for (let i = 0; i < stack.length; i++) {
          this._addNavHoverList(stack[i]);
        }
      }
    }

    /**
     * Nav Item link focus out.
     * 
     * @param {Event} ev 
     */
    _onNavLinkFocusOut(ev) {
      // let the focusout event fired first
      window.setTimeout(() => {
        // after focusout, check current's activeElement
        const t = $(document.activeElement);
        const nav = this.nav;

        if (t[0] === nav[0] || $.contains(nav[0], t[0])) {
          return;
        }

        // current activeElement is not the Main Nav or it's decendent
        this._clearNavHoverList();
      });
    }

    /**
     * Search form focus out.
     * 
     * @param {Event} ev 
     */
    _onSearchFormFocusOut(ev) {
      // let the focusout fired first
      window.setTimeout(() => {
        // after focusout, check current's activeElement
        const t = $(document.activeElement);
        const searchForm = this.searchForm;
        const toggleButton = this.flyoutSearchToggleButton;

        if (t[0] === searchForm[0] || $.contains(searchForm[0], t[0]) || t[0] === toggleButton[0] || $.contains(toggleButton[0], t[0])) {
          return;
        }

        // current activeElement is not the Search Form or it's decendent, neither the Search button
        // close Search Form
        this._toggleSearch(false);
      });
    }

    /**
     * Toggle Mobile Nav.
     * 
     * @param {Boolean} [force] if defined, will force Mobile Mode on/off. Otherwise will toggle the mode.
     */
    _toggleMobileNav(force) {
      const elHtml = $(document.documentElement);

      if (force === undefined) {
        force = !elHtml.hasClass(CSS_MOBILE_NAV_ACTIVE);
      }

      if (this.isMobile()) {
        this.flyout.addClass(CSS_EXPANDING);
        reflowElement(this.flyout);
        
        window.requestAnimationFrame(() => {
          elHtml.toggleClass(CSS_MOBILE_NAV_ACTIVE, force);
          transitionEnd(this.flyout, () => {
            this.flyout.removeClass(CSS_EXPANDING);
          });
        });
      }
      else {
        elHtml.toggleClass(CSS_MOBILE_NAV_ACTIVE, force);
      }

      if (force) {
        this.flyout.removeAttr(ARIA_HIDDEN);
      }
      else {
        if (this.isMobile()) {
          this.flyout.attr(ARIA_HIDDEN, true);
        }
        else {
          this.flyout.removeAttr(ARIA_HIDDEN);
        }
      }

      this._entrapFocus(force);
    }

    /**
     * Entrap focus inside the Mobile Nav when it's open.
     * 
     * @param {Boolean} entrap 
     */
    _entrapFocus(entrap) {
      if (!this.__entrapFocusHandler) {
        this.__entrapFocusHandler = this._entrapFocusHandler.bind(this);
      }

      if (!entrap) {
        $(document).off('focusout', this.__entrapFocusHandler);
        this._entrapingFocus = false;
        return;
      }

      if (!this._entrapingFocus) {
        this._entrapingFocus = true;
        $(document).on('focusout', this.__entrapFocusHandler);
      }
    }

    /**
     * Entrap focus functionality focusout handler.
     * 
     * @param {Event} ev 
     */
    _entrapFocusHandler(ev) {
      window.setTimeout(() => {
        const activeElement = document.activeElement;

        if (this.isMobile() && this.element[0] !== activeElement && !$.contains(this.element[0], activeElement)) {
          $('.header--logo a', this.element).first().get(0).focus();
        }
      });
    }

    /**
     * Toggle Search Form.
     * 
     * @param {Boolean} [force] if defined, will force Search on/off. Otherwise will toggle the mode.
     */
    _toggleSearch(force, cbShown, ev) {
      const el = this.element;
      const searchToggleBtn = this.flyoutSearchToggleButton;
      const elHtml = $(document.documentElement);

      if (force === undefined) {
        force = !searchToggleBtn.hasClass(CSS_ACTIVE);
      }

      this.searchFormContainer.addClass(CSS_EXPANDING);
        
      reflowElement(this.searchFormContainer);

      searchToggleBtn.toggleClass(CSS_ACTIVE, force);
      el.toggleClass(CSS_SEARCH_ACTIVE, force);

      // reset display
      this.flyout.css('display', '');
      this.mobileNavToggleButton.css('display', '');
      this.mobileSearchCloseButton.css('display', '');
      
      transitionEnd(this.searchFormContainer, () => {
        this.searchFormContainer.removeClass(CSS_EXPANDING);
        // Search Form is now open:
        //  - focus the first input element
        //  - close all Main Nav menu
        if (searchToggleBtn.hasClass(CSS_ACTIVE)) {
          // clear nav hover states
          this._clearNavHoverList();

          if (typeof cbShown === 'function') {
            cbShown();
          }
        }

        if (this.isMobile()) {
          if (force) {
            // hide the flyout and nav toggle button to prevent them from getting focus
            this.flyout.hide();
            this.mobileNavToggleButton.hide();
          }
          else {
            this.mobileSearchCloseButton.hide();
          }
        }
      });

      if (force) {
        this.searchFormContainer.removeAttr(ARIA_HIDDEN);

        if (this.isMobile()) {
          this.mobileSearchCloseButton.removeAttr(ARIA_HIDDEN);

          this.flyout.attr(ARIA_HIDDEN, true);
          this.mobileNavToggleButton.attr(ARIA_HIDDEN, true);

          this.smcStop(searchToggleBtn, this.searchFormContainer);
        }
        else {
          if (SEARCH_MOVEMENT_CONE) {
            this.smcStart(ev, searchToggleBtn, this.searchFormContainer);
          }
        }
      }
      else {
        this.smcStop(searchToggleBtn, this.searchFormContainer);
        this.searchFormContainer.attr(ARIA_HIDDEN, true);

        if (this.isMobile()) {
          this.mobileSearchCloseButton.attr(ARIA_HIDDEN, true);

          if (elHtml.hasClass(CSS_MOBILE_NAV_ACTIVE)) {
            this.flyout.removeAttr(ARIA_HIDDEN);
            this.mobileNavToggleButton.removeAttr(ARIA_HIDDEN);
          }
        }
      }

      
    }

    /**
     * Get/Set transition data from
     * 
     * @param {HTMLElement} element 
     * @param {Object|null|undefined} transitionEnd object
     */
    _elementTransition(element, value) {
      if (value) {
        $(element).data(DATA_ELEMENT_TRANSITION, value);
        return value;
      }

      return $(element).data(DATA_ELEMENT_TRANSITION);
    }

    /**
     * Toggle Nav Item CSS_HOVER class
     * 
     * @param {HTMLElement} item
     * @param {Boolean} [force] TRUE to show, FALSE to hide, otherwise toggle.
     * @param {Event} [ev] jQuery Event
     */
    _toggleItemHover(item, force, ev) {
      item = $(item);
      const isParent = item.hasClass(CSS_PARENT);
      const isHover = item.hasClass(CSS_HOVER);

      if (force === undefined) {
        force = !(isHover);
      }

      item.toggleClass(CSS_HOVER, !!force);

      if (isParent) {
        const menu = $('> ul', item);
        const transition = this._elementTransition(menu);

        if (force) {
          if (transition) {
            transition.abort();
          }

          menu.addClass(CSS_HOVERING);
          menu.removeClass(CSS_HOVERING_OFF);

          reflowElement(menu);

          this._elementTransition(menu, transitionEnd(menu, () => {
            if (menu.hasClass(CSS_HOVERING)) {
              menu.removeClass(CSS_HOVERING);
              menu.css({
                'opacity': ''
              });
            }
          }));

          menu.css({
            'opacity': 1
          });

          if (!this.isMobile() && SEARCH_MOVEMENT_CONE) {
            this.smcStart(ev, item, menu);
          }
        }
        else {
          // this.smcStop(item);

          if (transition) {
            transition.abort();
          }

          menu.addClass(CSS_HOVERING_OFF);
          menu.removeClass(CSS_HOVERING);

          reflowElement(menu);

          this._elementTransition(menu, transitionEnd(menu, () => {
            if (menu.hasClass(CSS_HOVERING_OFF)) {
              menu.removeClass(CSS_HOVERING_OFF);
              menu.css({
                'opacity': ''
              });
            }
          }));

          menu.css({
            'opacity': 0
          });
        }

        if (!this.isMobile() && force) {
          menu.removeAttr(ARIA_HIDDEN);
        }
        else {
          menu.attr(ARIA_HIDDEN, true);
        }
      }
    }

    /**
     * Toggle Nav Item CSS_EXPAND class
     * 
     * @param {HTMLElement} item
     * @param {Boolean} [force] 
     */
    _toggleItemExpand(item, force) {
      item = $(item);
      const isParent = item.hasClass(CSS_PARENT);
      const isExpand = item.hasClass(CSS_EXPAND);

      if (force === undefined) {
        force = !(isExpand);
      }

      if (isParent) {
        item.toggleClass(CSS_EXPAND, !!force);

        const menu = $('> ul', item);
        const transition = this._elementTransition(menu);

        if (force) {
          if (transition) {
            transition.abort();
          }

          menu.addClass(CSS_EXPANDING);
          menu.removeClass(CSS_EXPANDING_OFF);

          let height = menu[0].scrollHeight;

          reflowElement(menu);

          this._elementTransition(menu, transitionEnd(menu, () => {
            if (menu.hasClass(CSS_EXPANDING)) {
              menu.removeClass(CSS_EXPANDING);
              menu.css({
                'height': ''
              });
            }
          }));

          menu.css({
            'height': height
          });
        }
        else {
          if (transition) {
            transition.abort();
          }

          menu.addClass(CSS_EXPANDING_OFF);
          menu.removeClass(CSS_EXPANDING);

          menu.css('height', menu.height());
          reflowElement(menu);

          this._elementTransition(menu, transitionEnd(menu, () => {
            if (menu.hasClass(CSS_EXPANDING_OFF)) {
              menu.removeClass(CSS_EXPANDING_OFF);
              menu.css({
                'height': ''
              });
            }
          }));

          menu.css({
            'height': 0
          });
        }

        if (this.isMobile() && force) {
          menu.removeAttr(ARIA_HIDDEN);
        }
        else {
          menu.attr(ARIA_HIDDEN, true);
        }
      }
    }

    /**
     * Add nav item to internal hover list.
     * 
     * @param {jQuery} item nav item.
     * @param {Event} [ev] jQuery Event.
     */
    _addNavHoverList(item, ev) {
      let i = 0;

      // ignore mega menu column <LI>
      if (this.megaMenuItems.index(item) >= 0) {
        return;
      }

      while (i < this.navHoverList.length) {
        const _item = this.navHoverList[i];

        // when an item enters hover state, 
        //   remove hover state from other items that are part of this item's tree
        if (!(_item[0] === item[0] || $.contains(_item[0], item[0]))) {
          this.navHoverList.splice(i, 1);
          this._toggleItemHover(_item, false);
          continue;
        }

        i++;
      }

      this._toggleItemHover(item, true, ev);
      this.navHoverList.push(item);
    }

    /**
     * Remove nav item to internal hover list.
     * 
     * @param {jQuery} item nav item.
     */
    _removeNavHoverList(item) {
      // ignore mega menu column <LI>
      if (this.megaMenuItems.index(item) >= 0) {
        return;
      }

      let i = 0;

      while (i < this.navHoverList.length) {
        const _item = this.navHoverList[i];

        // when an item leaves hover state, 
        //   remove hover state from it and it's items tree
        if (_item[0] === item[0] || $.contains(item[0], _item[0])) {
          this.navHoverList.splice(i, 1);
          this._toggleItemHover(_item, false);
          continue;
        }

        i++;
      }
    }

    /**
     * Clear nav item to internal hover list.
     * 
     * @param {jQuery} item nav item.
     */
    _clearNavHoverList(item) {
      for (let i = 0; i < this.navHoverList.length; i++) {
        this._toggleItemHover(this.navHoverList[i], false);
      }

      this.navHoverList = [];
    }

    /**
     * Resize event handler.
     */
    _onResize(ev, initial) {
      const isMobile = this.isMobile();
      const clickMode = (this.noHover || isMobile);
      const modeChanged = (this.wasMobile !== isMobile);
      const clickModeChanged = (this.wasClickMode !== clickMode);

      this.wasMobile = isMobile;
      this.wasClickMode = clickMode;

      if (!this.__onNavItemLinkPointerUp) {
        this.__onNavItemLinkPointerUp = this._onNavItemLinkPointerUp.bind(this);
        this.__onNavItemMouseEnter = this._onNavItemMouseEnter.bind(this);
        this.__onNavItemMouseLeave = this._onNavItemMouseLeave.bind(this);
      }

      if (modeChanged) {
        document.documentElement.style.setProperty('--header-height', this.element.closest('header').height() + 'px');

        if (clickModeChanged) {
          // devices without hover OR Mobile
          if (clickMode) {
            this.nav.off('mouseenter', 'li', this.__onNavItemMouseEnter);
            this.nav.off('mouseleave', 'li', this.__onNavItemMouseLeave);
            this.flyoutSearch.off('mousemove');

            this.nav.on('pointerup', 'a', this.__onNavItemLinkPointerUp);
          }
          // devices with hover AND Desktop
          else {
            this.nav.off('pointerup', 'a', this.__onNavItemLinkPointerUp);

            this.nav.on('mouseenter', 'li', this.__onNavItemMouseEnter);
            this.nav.on('mouseleave', 'li', this.__onNavItemMouseLeave);

            this.flyoutSearch.on('mousemove', HeaderMain.selectors.flyoutSearchToggleButton, (ev) => {
              if (this._smcSuppressMenuHover || this.element.hasClass(CSS_SEARCH_ACTIVE)) {
                this._smcSuppressedMenuHover = ev;
                this.smcStart(ev, this.flyoutSearchToggleButton, this.searchFormContainer);
              }
            });
          }
        }

        if (!initial) {
          // reset states on mode change
          $('li', this.nav).removeClass([CSS_HOVER, CSS_EXPAND]);
          $('ul', this.nav).removeClass([CSS_HOVERING, CSS_HOVERING_OFF, CSS_EXPANDING, CSS_EXPANDING_OFF]);
          this._toggleMobileNav(false);
          this._toggleSearch(false);

          // reset aria-hidden
          $('li', this.nav).filter((idx, elem) => $(elem).hasClass(CSS_PARENT)).find('> ul').attr(ARIA_HIDDEN, true);
        }

        if (this.isMobile()) {
          this.flyout.attr('aria-hidden', 'true');
          this.flyoutSearch.insertBefore(this.flyoutNav);
          this.flyoutSocial.removeAttr(ARIA_HIDDEN);
        }
        else {
          this.flyout.attr('aria-hidden', 'false');
          this.flyoutSearch.insertAfter(this.flyoutNav);
          this.flyoutSocial.attr(ARIA_HIDDEN, true);
        }

        // reinit focus UX
        this.initFocus();
      }
    }

    /**
     * Nav item mouseenter event handler.
     * Only on Desktop AND with hover.
     * 
     * @param {Event} ev 
     */
    _onNavItemMouseEnter(ev) {
      if (this._smcSuppressMenuHover) {
        this._smcSuppressedMenuHover = ev;
        this.smcSetTimer(ev);
        return;
      }

      const t = $(ev.currentTarget);
      this._addNavHoverList(t, ev);

      // toggle the search off
      this._toggleSearch(false);
    }

    /**
     * Nav item mouseleave event handler.
     * Only on Desktop AND with hover
     * 
     * @param {Event} ev 
     */
    _onNavItemMouseLeave(ev) {
      if (this._smcSuppressMenuHover) {
        this._smcSuppressedMenuHover = null;
        // this.smcSetTimer();
        return;
      }

      const t = $(ev.currentTarget);
      this._removeNavHoverList(t);

      const p = t.parent().parent();
      if (p.length) {
        this._addNavHoverList(p, ev);
      }
    }

    /**
     * Nav item link pointerup event handler.
     * Nav item link focusin event handler also use this part of this logic.
     * Only on Mobile OR without hover.
     * 
     * @param {Event} ev 
     */
    _onNavItemLinkPointerUp(ev) {
      const t = $(ev.currentTarget);
      const li = t.closest('li');

      if (this.__cancelClick) {
        t.off('click', this.__cancelClick);
      }

      // handle: parent items
      if (li.hasClass(CSS_PARENT)) {
        // on Mobile:
        //   - always cancel the click
        //   - always toggle expand/collapse
        if (this.isMobile()) {
          if (ev.type === 'pointerup') {
            ev.preventDefault();
            ev.stopPropagation();
            this._cancelNextClick(t);
          }

          this._toggleItemExpand(li);
        }
        // on Desktop:
        //   - if it's currently not in hover state, show sub items and cancel the click.
        //   - if it's currently on hover state, let it click through.
        else {
          if (!li.hasClass(CSS_HOVER)) {
            if (ev.type === 'pointerup') {
              ev.preventDefault();
              this._cancelNextClick(t);
            }

            this._addNavHoverList(li);

            // toggle the search off
            this._toggleSearch(false);
          }
        }
      }
      // handle: links
      else {
        if (this.isMobile()) {
          // just leave it, not need to collapse other
          return;
        }
        
        // close all siblings menu
        li.siblings().each((idx, sibling) => {
          this._removeNavHoverList($(sibling));
        });

        if (!this.noHover && li.hasClass(CSS_PARENT)) {
          this._addNavHoverList(li);
        }
      }
    }

    /**
     * Document pointerdown/touchstart event handler.
     * Handle navigation (desktop AND no hover) auto close, search form auto close (desktop)/
     * 
     * @param {PointerEvent} ev PointerEvent. Note that this is not a jQuery wrapped Event.
     */
    _onDocumentPointerDown(ev) {
      if (!this.isMobile()) {
        const t = $(ev.target);
        const flyout = this.flyout;
        const searchForm = this.searchForm;

        // if the element is the header flyout or the decendant of the it, skip
        if (t[0] === flyout[0] || $.contains(flyout[0], t[0])) {
          return;
        }

        // if the element is the header search form or the decendant of it, skip
        if (t[0] === searchForm[0] || $.contains(searchForm[0], t[0])) {
          return;
        }

        // close search form
        if (ev.type === 'pointerdown') {
          this._toggleSearch(false);
        }

        // for Desktop nav click move, close all active nav
        this._clearNavHoverList();
      }
    }

    /**
     * Cancel click (once) on specified element that happens *after* pointerup event.
     * Should only call this from _onNavItemLinkPointerUp().
     * 
     * @param {jQuery} t 
     */
    _cancelNextClick(t) {
      if (!this.__cancelClick) {
        this.__cancelClick = (ev) => {
          ev.preventDefault();
        };
        this.__cancelClick = this.__cancelClick.bind(this);
      }
      
      t.one('click', this.__cancelClick);
    }
  }

  HeaderMain.selectors = {
    flyout: '.header--flyout',
    flyoutNav: '.header--nav',
    flyoutSearch: '.header--search',
    flyoutSearchToggleButton: '.header--search-toggle',
    flyoutSocial: '.header--social',
    mobileNavToggleButton: '.header--nav-toggle',
    mobileSearchCloseButton: '.header--search-form-toggle',
    searchFormContainer: '.header--search-form',
    searchForm: '.header--search-form form',
    searchFormBackButton: '.header--search-form-close-btn',
    searchFormInput: '.header--search-form input[type="search"]',
    nav: '.header--nav > nav',
    megaMenuItems: '.header--nav-megamenu-items'
  };

  return HeaderMain;
})(jQuery, window);